import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { darkBiome } from '../biomeArchetypes';
import { ATMOSPHERE_PALETTE as P } from '../palette';

/** Abyss / Sunless Deep — a pitch-dark eerie chasm (NOT underwater: a dry void). The DARKEST
 *  biome — near-black sky, almost no light, and only sparse cold blue/green bioluminescent glints
 *  far off in the dark: a faint distant lure-glow, a few drifting biolume specks, dim cold shafts,
 *  a slow deep-floor glow that breathes, a sparse cold light bed, slow rising cold motes, faint
 *  wall glints, jagged near-black chasm-wall silhouettes (far/mid/near), a thin cold haze, a HEAVY
 *  oppressive vignette (claustrophobic), and a dark cold grade. Contrast is the whole point — the
 *  scene stays black so the sparse glints read; the grade darkens ('normal'), never washes. */
export const ABYSS = darkBiome({
  id: 'abyss',
  sky: { from: 0x02030a, to: 0x06141f },
  celestial: { atX: 0.5, atY: 0.22, color: P.deepTeal, haloColor: 0x2f8fa0, size: 58, haloScale: 3.2, coreAlpha: 0.2, haloAlpha: 0.05 },
  motes: { colors: [P.deepTeal, P.crystal, P.mushroomBlue], count: 14, sizeMin: 3, sizeMax: 9, driftPx: 30, periodMinMs: 6000, periodMaxMs: 14000, alphaMin: 0.12, alphaMax: 0.55 },
  rays: { color: P.frostCore, count: 2, widthMax: 120, tiltDeg: 5, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.04, alphaMax: 0.16 },
  glow: {
    blobs: [
      { color: P.abyssBlue, atX: 0.5, atY: 0.97, size: 600, alpha: 0.12 },
      { color: P.deepTeal, atX: 0.5, atY: 0.1, size: 360, alpha: 0.05 },
    ],
    breathe: { periodMs: 5600, min: 0.6, max: 1.3 },
  },
  lightField: { palette: [P.abyssBlue, P.deepTeal, P.crystal], count: 10, sizeMin: 120, alphaMin: 0.03, alphaMax: 0.1, driftRadius: 70, periodMinMs: 7000, periodMaxMs: 14000 },
  extraAtmosphereLayers: [
    // Cold bioluminescent cells pulsing in the dark — sparse, slow, eerie deep-sea
    // organisms swelling + receding. Saturated cold teal/crystal + alphaMax 0.55 so
    // the glints read against the near-black void (the read rule: ≥~0.5 alpha over
    // dark skies). Distinct from the fireflies (twinkling specks) — these are larger,
    // slower, organic pulses.
    {
      kind: 'bioluminescence', color: P.deepTeal, count: 5,
      sizeMin: 60, sizeMax: 140, alphaMin: 0.15, alphaMax: 0.55,
      bandYMin: 0.25, bandYMax: 0.8, driftPx: 12,
      periodMinMs: 6000, periodMaxMs: 12000,
    },
  ],
  weather: { mode: 'rise', color: P.deepTeal, colorEnd: 0x0a2630,
    size: 8, frequencyMs: 200, lifespanMs: 4200, speed: 22, gravityY: -12, windX: 8, alphaEnd: 0.4,
    gust: { periodMs: 7000, min: 0.4, max: 1.4, phase: 0.25 } },
  flicker: { color: P.crystal, size: 240, baseAlpha: 0.05, flickerAmp: 0.05, hzMin: 2, hzMax: 6, anchors: [{ atX: 0.18, atY: 0.92 }, { atX: 0.5, atY: 0.96 }, { atX: 0.82, atY: 0.9 }] },
  parallax: [
    { svg: OTHERWORLD_BACKDROPS.abyss.far, shape: 'spires', color: 0x081821, speed: 0.04, height: 460, alpha: 0.78, detail: 0.9, sway: { ampPx: 10, periodMs: 28000 } },
    { svg: OTHERWORLD_BACKDROPS.abyss.mid, shape: 'spires', color: 0x040e15, speed: 0.10, height: 320, alpha: 0.88, detail: 1.2 },
    { svg: OTHERWORLD_BACKDROPS.abyss.near, shape: 'peaks', color: 0x01060a, speed: 0.20, height: 200, alpha: 0.96, detail: 1 },
  ],
  fog: { color: P.deepTeal, bands: 4, alphaMin: 0.03, alphaMax: 0.09, driftPx: 50, periodMinMs: 10000, periodMaxMs: 17000 },
  closer: {
    vignette: { color: 0x01030a, edgeAlpha: 0.5, washColor: 0x103040, washAlpha: 0.06 },
    grade: { top: 0x12384a, bottom: 0x010308, strength: 0.12, blend: 'normal' },
  },
});
